import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';

import { PasswordInput } from '@/shared/forms/password-input';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { AuthPage } from '../components/auth-page';
import { ServerError } from '../components/server-error';
import { useResetPassword } from '../hooks/use-reset-password';
import { resetPasswordSchema, type ResetPasswordValues } from '../schemas/password-schemas';

export interface ResetPasswordPageProps {
  /** From the reset email; missing or rejected tokens render the expired state. */
  token: string | undefined;
}

export function ResetPasswordPage({ token }: ResetPasswordPageProps) {
  const { t } = useTranslation('auth');
  const reset = useResetPassword();
  const form = useForm<ResetPasswordValues>({
    resolver: useI18nResolver(resetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const signIn = <Link to="/login">{t('links.signIn')}</Link>;
  const tokenRejected = isApiError(reset.error) && reset.error.kind === 'unauthorized';

  if (!token || tokenRejected) {
    return (
      <AuthPage
        title={t('reset.expiredTitle')}
        description={token ? t('reset.expired') : t('reset.missingToken')}
        footer={signIn}
      >
        <Button asChild size="lg" className="h-11 w-full">
          <Link to="/forgot-password">{t('reset.requestNew')}</Link>
        </Button>
      </AuthPage>
    );
  }

  if (reset.isSuccess) {
    return (
      <AuthPage title={t('reset.successTitle')} description={t('reset.success')}>
        <Button asChild size="lg" className="h-11 w-full">
          <Link to="/login">{t('links.signIn')}</Link>
        </Button>
      </AuthPage>
    );
  }

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await reset.mutateAsync({ token, password: values.password });
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  return (
    <AuthPage title={t('reset.title')} description={t('reset.description')} footer={signIn}>
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          {reset.isError && !reset.error.hasFieldErrors ? (
            <ServerError error={reset.error} />
          ) : null}
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.newPassword')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="new-password"
                    autoFocus
                    className="h-11"
                    {...field}
                  />
                </FormControl>
                <FormDescription>{t('accept.passwordHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.confirmPassword')}</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="new-password" className="h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" size="lg" className="h-11 w-full" disabled={reset.isPending}>
            {reset.isPending ? t('reset.submitting') : t('reset.submit')}
          </Button>
        </form>
      </Form>
    </AuthPage>
  );
}
